// Inline desktop variant of the "Customize your outfit" composer.
// Renders templates + prompt as a section in the right column — no modal.
import { useState } from "react";
import { Wand2, Loader2, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { OUTFIT_TEMPLATES, type OutfitTemplate } from "@/lib/outfit-templates";
import type { CustomizeData } from "@/components/CustomizeOutfitDialog";

interface Props {
  busy: boolean;
  onGenerate: (data: CustomizeData) => void | Promise<void>;
}

export const CustomizeOutfitInline = ({ busy, onGenerate }: Props) => {
  const [prompt, setPrompt] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);

  const pickTemplate = (t: OutfitTemplate) => {
    if (selectedTemplate === t.id) {
      setSelectedTemplate(null);
      setPrompt("");
    } else {
      setSelectedTemplate(t.id);
      setPrompt(t.prompt);
    }
  };

  const submit = async () => {
    const payload = { prompt: prompt.trim(), referenceLinks: [], referenceFiles: [] };
    await onGenerate(payload);
  };

  const selected = selectedTemplate ? OUTFIT_TEMPLATES.find((t) => t.id === selectedTemplate) : null;

  return (
    <div className="rounded-3xl border border-white/10 bg-card/60 p-6 shadow-elevated backdrop-blur-xl xl:p-8">
      <div className="mb-6">
        <span className="font-display text-[10px] font-semibold uppercase tracking-[0.3em] text-primary">
          — Custom
        </span>
        <h2 className="font-display mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight">
          <Wand2 className="h-5 w-5 text-primary" /> Customize your outfit
        </h2>
        <p className="mt-1 text-sm text-muted-foreground">
          Pick a template or write a prompt — your face stays exactly the same.
        </p>
      </div>

      <div className="space-y-5">
        <div>
          <Label className="flex items-center gap-1.5 text-sm font-semibold">
            <Sparkles className="h-3.5 w-3.5 text-primary" /> Style templates
          </Label>
          <p className="mb-2 mt-0.5 text-xs text-muted-foreground">
            One-tap looks. Tap again to clear.
          </p>
          <div className="flex flex-wrap gap-1.5">
            {OUTFIT_TEMPLATES.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => pickTemplate(t)}
                className={cn(
                  "rounded-full border px-3 py-1.5 text-xs font-medium transition-all",
                  selectedTemplate === t.id
                    ? "border-primary bg-primary text-primary-foreground shadow-sm"
                    : "border-border bg-background hover:border-primary/50 hover:bg-accent",
                )}
              >
                <span className="mr-1">{t.emoji}</span>{t.label}
              </button>
            ))}
          </div>

          {selected && (
            <div className="mt-3 animate-fade-in overflow-hidden rounded-xl border border-primary/30 bg-card shadow-soft">
              <div className="relative aspect-[4/3] w-full bg-muted">
                <img
                  src={selected.image}
                  alt={`${selected.label} style reference`}
                  className="absolute inset-0 h-full w-full object-cover"
                />
                <div className="absolute left-2 top-2 rounded-full bg-black/70 px-2.5 py-1 text-[10px] font-semibold uppercase tracking-wide text-white">
                  <span className="mr-1">{selected.emoji}</span>
                  Preview · {selected.label}
                </div>
              </div>
              <p className="px-3 py-2 text-[11px] text-muted-foreground">
                Your face &amp; body stay yours — this is just the style reference.
              </p>
            </div>
          )}
        </div>

        <div>
          <Label htmlFor="customize-inline-prompt" className="text-sm font-semibold">
            Style prompt (optional)
          </Label>
          <p className="mb-2 mt-0.5 text-xs text-muted-foreground">
            Fine-tune the vibe — overrides template.
          </p>
          <Textarea
            id="customize-inline-prompt"
            name="customizeInlinePrompt"
            autoComplete="off"
            value={prompt}
            onChange={(e) => { setPrompt(e.target.value); setSelectedTemplate(null); }}
            placeholder="e.g. streetwear with sneakers, neutral colors, going to a beach party…"
            maxLength={500}
            rows={4}
          />
        </div>
      </div>

      <div className="mt-6 space-y-1.5 border-t border-white/10 pt-5">
        <Button
          className="h-12 w-full gradient-primary text-primary-foreground"
          onClick={submit}
          disabled={busy}
        >
          {busy ? (
            <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Styling full look…</>
          ) : (
            <><Wand2 className="mr-2 h-4 w-4" /> Generate (1 credit)</>
          )}
        </Button>
        <p className="text-center text-[11px] text-muted-foreground">
          Usually 30–60s · Style Score after is free · Refunded if it fails
        </p>
      </div>
    </div>
  );
};
